// §V2 "Quiet Foundry" — prompt classification for the generation wait
// screen (ported from the pomela-platform-v2 design spike).
//
// While the worker builds the real app, the wait screen assembles a MOCK
// of the user's app inside a phone frame — in a category-appropriate
// accent, with category-appropriate blocks, seeded with nouns mined from
// the user's own prompt ("it read me" proof). This is honest decoration:
// it never claims to be the real render — the real app arrives only with
// deploy_complete — but it makes the wait read as creation, not loading.
//
// Deterministic + dependency-free on purpose: same prompt → same scene.

export type FoundryBlock =
  | 'slots' // bookable time chips (booking)
  | 'stats' // metric tiles (tracker / finance)
  | 'rsvp' // event card + attendance row (events)
  | 'rows' // avatar list rows (directory / CRM)
  | 'products' // 2-up product tiles (commerce)
  | 'cards' // stacked flip cards (flashcards)
  | 'hero'; // generic hero band

export interface FoundryScene {
  /** Category key — also the data-category attribute for styling hooks. */
  category: string;
  /** The single chroma of the scene (the app's provisional accent). */
  accent: string;
  /** Ordered blocks that assemble inside the phone, stage by stage. */
  blocks: FoundryBlock[];
  /** Tab labels for the phone's mock tab bar (i18n-neutral nouns from the
   *  prompt when available, else category defaults). */
  tabs: string[];
}

interface CategoryDef {
  accent: string;
  blocks: FoundryBlock[];
  tabs: string[];
  /** Hebrew tab defaults — used when the PROMPT itself is Hebrew, so the
   *  mock never mixes scripts with the user's own language. */
  tabsHe: string[];
  match: RegExp;
}

// Order matters — first match wins. Patterns cover EN + HE keywords the
// composer sees most (mirrors request-router's alias spirit, but this is
// pure presentation — misclassification costs a wrong mock accent, nothing
// else).
const CATEGORIES: Array<[string, CategoryDef]> = [
  ['booking', {
    accent: '#E0457B',
    blocks: ['hero', 'slots', 'rows'],
    tabs: ['Book', 'Visits', 'Profile'],
    tabsHe: ['תורים', 'ביקורים', 'פרופיל'],
    match: /\b(book|booking|appointment|salon|barber|studio|reserve|reservation|clinic|class(es)?)\b|תור|הזמנ|סטודיו|מספר[הת]|קליניק/i
  }],
  ['tracker', {
    accent: '#16A34A',
    blocks: ['stats', 'hero', 'rows'],
    tabs: ['Today', 'Trends', 'Goals'],
    tabsHe: ['היום', 'מגמות', 'יעדים'],
    match: /\b(track|tracker|habit|water|steps|log|diary|journal|workout|weight)\b|מעקב|הרגל|אימונ|יומן|צעדים|שתי(י)?ה/i
  }],
  ['events', {
    accent: '#7C4DFF',
    blocks: ['hero', 'rsvp', 'rows'],
    tabs: ['Event', 'Guests', 'Updates'],
    tabsHe: ['אירוע', 'אורחים', 'עדכונים'],
    match: /\b(event|rsvp|invite|party|wedding|meetup|conference)\b|אירוע|הזמנה לאירוע|חתונה|מסיב|כנס/i
  }],
  ['directory', {
    accent: '#0891B2',
    blocks: ['hero', 'rows', 'rows'],
    tabs: ['Browse', 'Saved', 'About'],
    tabsHe: ['עיון', 'שמורים', 'אודות'],
    match: /\b(director(y|ies)|listing|catalog|members?|contacts?|team|crm|customers?)\b|ספריי?ה|אנשי קשר|לקוחות|קטלוג/i
  }],
  ['finance', {
    accent: '#0D9488',
    blocks: ['stats', 'rows', 'hero'],
    tabs: ['Overview', 'Activity', 'Reports'],
    tabsHe: ['סקירה', 'פעילות', 'דוחות'],
    match: /\b(expense|budget|invoice|finance|money|receipt|spend)\b|הוצא|תקציב|חשבונית|קבל[הו]|כספים/i
  }],
  ['commerce', {
    accent: '#0F9D6E',
    blocks: ['hero', 'products', 'rows'],
    tabs: ['Shop', 'Orders', 'Cart'],
    tabsHe: ['חנות', 'הזמנות', 'סל'],
    match: /\b(shop|store|order|menu|sell|product|cart|bakery|restaurant|cafe)\b|חנות|הזמנות אוכל|תפריט|מוצר|מאפי|מסעד/i
  }],
  ['flashcards', {
    accent: '#4F46E5',
    blocks: ['hero', 'cards', 'stats'],
    tabs: ['Study', 'Decks', 'Progress'],
    tabsHe: ['תרגול', 'חפיסות', 'התקדמות'],
    match: /\b(flash ?cards?|study|quiz|vocab|learn(ing)? deck)\b|כרטיסיו|חידון|לימוד|שינון/i
  }],
];

const GENERIC: FoundryScene = {
  category: 'generic',
  accent: '#F4477E',
  blocks: ['hero', 'rows', 'stats'],
  tabs: ['Home', 'Activity', 'More']
};

const STOP = new Set(
  ('a an the for my our your of and or to with that this app application website page site make build create want need ' +
    'אפליקציה אפליקצייה אתר דף בשביל עבור שלי שלנו עם וגם או את'
  ).split(' ')
);

/** Mine up to `n` content nouns from the prompt (the "it read me" seed). */
export function mineNouns(prompt: string, n = 3): string[] {
  return prompt
    .split(/[^\p{L}\p{N}']+/u)
    .filter((w) => w.length > 2 && !STOP.has(w.toLowerCase()))
    .slice(0, n)
    .map((w) => w[0]!.toUpperCase() + w.slice(1));
}

/** Classify a prompt into a foundry scene. Deterministic, best-effort.
 *  A Hebrew prompt gets Hebrew tab defaults — the mock speaks the user's
 *  own language, never a mixed-script one. */
export function classifyPrompt(prompt: string): FoundryScene {
  const isHe = /[֐-׿]/.test(prompt);
  for (const [category, def] of CATEGORIES) {
    if (def.match.test(prompt)) {
      const base = isHe ? def.tabsHe : def.tabs;
      const nouns = mineNouns(prompt, 2);
      // Seed the first tab with the user's own noun when we have one —
      // the moment the scene visibly "read" their prompt.
      const tabs = nouns.length && nouns[0]!.length <= 10
        ? [nouns[0]!, ...base.slice(1)]
        : base;
      return { category, accent: def.accent, blocks: def.blocks, tabs };
    }
  }
  return isHe ? { ...GENERIC, tabs: ['בית', 'פעילות', 'עוד'] } : GENERIC;
}
